import React from "react";
import {
  Box,
  Typography,
  Paper,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Checkbox,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { GradientBorderTableContainer } from "../../../components/common/GradientBorderTableContainer";
import { ExportPagination } from "./ExportPagination";
import { getDisplayValue } from "../../../utils/formatter";
import { ExtractedContract } from "../../../types/contract.d";
import { NormalizedDataItem } from "../useExportData";

interface ExportTableProps {
  format: string;
  columnDefs: { key: string; label: string }[];
  results: NormalizedDataItem[];
  currentVisibleRows: NormalizedDataItem[];
  loading: boolean;
  contextError: string | null;
  clientError: string | null;
  selectedIds: Set<number>;
  selectAllMode: boolean;
  totalCount: number;
  numSelected: number;
  rowCount: number;
  anchorEl: HTMLElement | null;
  page: number;
  rowsPerPage: number;
  sortOrder: 'asc' | 'desc';
  dataMode: 'full' | 'paginated' | null;
  itandiLoading: boolean;
  onSelectRow: (id: number) => void;
  onSelectAllClick: () => void;
  onMenuClick: (event: React.MouseEvent<HTMLElement>) => void;
  onMenuClose: () => void;
  onSelectAllMenuItems: () => void;
  onSelectPageMenuItems: () => void;
  onDeselectAll: () => void;
  onPageChange: (event: unknown, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSortChange: (newOrder: 'asc' | 'desc') => void;
}

export const ExportTable: React.FC<ExportTableProps> = ({
  format,
  columnDefs,
  results,
  currentVisibleRows,
  loading,
  contextError,
  clientError,
  selectedIds,
  selectAllMode,
  totalCount,
  numSelected,
  rowCount,
  anchorEl,
  page,
  rowsPerPage,
  sortOrder,
  dataMode,
  itandiLoading,
  onSelectRow,
  onSelectAllClick,
  onMenuClick,
  onMenuClose,
  onSelectAllMenuItems,
  onSelectPageMenuItems,
  onDeselectAll,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
}) => {
  return (
    <Paper sx={{ p: 2, mb: 0, backgroundColor: "transparent", boxShadow: "none" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexShrink: 0 }}>
        <Typography variant="h6" gutterBottom>
          <span style={{ color: "rgb(50, 225, 170)", fontWeight: "bold", fontSize: "1.5rem" }}>
            {format === 'itandi' ? 'ステップ3' : 'ステップ2'}
          </span>
          <span style={{ color: "black", fontWeight: "500", fontSize: "1.4rem" }}>
            : 抽出データ確認
          </span>
        </Typography>
        {(selectAllMode || selectedIds.size > 0) && (
          <Typography variant="body2">
            {selectAllMode ? `全${totalCount}件選択中（全件）` : `${selectedIds.size}件選択中`}
          </Typography>
        )}
      </Box>
      {(contextError || clientError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {contextError || clientError}
        </Alert>
      )}
      <GradientBorderTableContainer>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "100px", padding: 0, backgroundColor: "transparent" }}>
                <Box sx={{ display: "flex", alignItems: "center", paddingLeft: "12px" }}>
                  <Checkbox
                    indeterminate={!selectAllMode && numSelected > 0 && numSelected < rowCount}
                    checked={selectAllMode || (rowCount > 0 && numSelected === rowCount)}
                    onChange={onSelectAllClick}
                  />
                  <IconButton size="small" onClick={onMenuClick}>
                    <ArrowDropDownIcon />
                  </IconButton>
                </Box>
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={onMenuClose}>
                  <MenuItem onClick={onSelectAllMenuItems}>すべて選択</MenuItem>
                  <MenuItem onClick={onSelectPageMenuItems}>このページを選択</MenuItem>
                  <MenuItem onClick={onDeselectAll}>すべての選択を解除</MenuItem>
                </Menu>
              </TableCell>
              {columnDefs.map((col) => (
                <TableCell key={col.key} sx={{ whiteSpace: "nowrap", backgroundColor: "transparent" }}>
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columnDefs.length + 1} align="center" sx={{ backgroundColor: "transparent" }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : results.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columnDefs.length + 1} align="center" sx={{ backgroundColor: "transparent" }}>
                  データがありません。
                </TableCell>
              </TableRow>
            ) : (
              currentVisibleRows.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  onClick={() => onSelectRow(row.id!)}
                  role="checkbox"
                  aria-checked={selectedIds.has(row.id!)}
                  tabIndex={-1}
                  selected={selectedIds.has(row.id!)}
                  sx={{
                    "&.Mui-selected": { backgroundColor: "rgba(50, 225, 170, 0.1)" },
                    "&:hover": { backgroundColor: "rgba(50, 225, 170, 0.05)" },
                  }}
                >
                  <TableCell sx={{ width: "100px", padding: "0 0 0 12px", backgroundColor: "transparent" }}>
                    <Checkbox checked={selectedIds.has(row.id!)} />
                  </TableCell>
                  {columnDefs.map((col) => {
                    let displayValue = 'N/A';
                    if (format === 'itandi') {
                      displayValue = getDisplayValue(row as ExtractedContract, col.key as keyof ExtractedContract);
                    } else {
                      const value = (row as any)[col.key];
                      displayValue = value !== null && value !== undefined ? String(value) : 'N/A';
                    }
                    return (
                      <TableCell
                        key={col.key}
                        sx={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: 250,
                          backgroundColor: "transparent",
                        }}
                      >
                        {displayValue}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </GradientBorderTableContainer>

      {results.length > 0 && (
        <ExportPagination
          format={format}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={totalCount}
          sortOrder={sortOrder}
          dataMode={dataMode}
          itandiLoading={itandiLoading}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          onSortChange={onSortChange}
        />
      )}
    </Paper>
  );
};
